/**
 * Portfolio.tsx — /app/portfolio  (v8.9.1)
 *
 * Portfolio Intelligence workspace — hardened.
 * Aggregates trade plans across watchlist into portfolio-level analysis.
 *
 * Sub-tabs: OVERVIEW | EXPOSURE | CONFLICTS | PRIORITY
 * - OVERVIEW: metrics, directional breakdown, guidance (separated from trace)
 * - EXPOSURE: raw + vol-adjusted risk units, vol fit indicators per plan
 * - CONFLICTS: severity-aware cards with vol_instability distinct styling
 * - PRIORITY: correlation cluster rank badge, allocation with rationale
 */
import React, { useState, useMemo } from 'react';
import { useStore }             from '../../store';
import { getSymbolContext }     from '../../selectors/symbolContext';
import { getThesis }            from '../../ai/thesisEngine';
import { buildExecutionPlan }   from '../../domains/execution/executionIntelligence';
import { evaluateExecution, type ExecutionFeedback } from '../../domains/execution/executionFeedback';
import {
  buildPortfolioIntelligence,
  type PortfolioIntelligenceOutput,
  type PrioritizedPlan,
  type ConflictSignal,
  type AllocationGuidance,
} from '../../domains/portfolio/portfolioIntelligence';
import type { RiskModelSettings } from '../../domains/risk/sizing/positionSizing';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  gold: '#C9A84C', border: '#1A3050', card: 'rgba(13,31,53,0.55)',
  text: '#E8EDF5', dim: '#4A6FA5', muted: '#3A5070',
  green: '#4ADE80', red: '#F87171', amber: '#FCD34D', blue: '#38BDF8',
  mono: "'Courier New', monospace",
};

function dirColor(d: string) { return d === 'long' ? C.green : d === 'short' ? C.red : C.dim; }
function concColor(level: string) {
  return level === 'critical' ? C.red : level === 'high' ? '#FCA5A5'
    : level === 'elevated' ? C.amber : C.green;
}
function sevColor(s: string) { return s === 'high' ? C.red : s === 'medium' ? C.amber : C.dim; }
function actionColor(a: string) {
  return a === 'full_size' ? C.green : a === 'half_size' ? C.amber
    : a === 'avoid' ? C.red : C.dim;
}

// ── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ intel }: { intel: PortfolioIntelligenceOutput }) {
  const { state } = intel;
  const adjDiff = state.totalAdjustedRisk !== state.totalRiskUnits;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { label: 'ACTIVE PLANS',   value: `${state.planCount}`,                              color: C.text },
          { label: 'RAW RISK UNITS', value: state.totalRiskUnits.toFixed(2),                   color: C.gold },
          { label: 'ADJ RISK UNITS', value: state.totalAdjustedRisk.toFixed(2),                color: adjDiff ? C.amber : C.gold },
          { label: 'CONCENTRATION',  value: state.concentrationRisk.toUpperCase(),             color: concColor(state.concentrationRisk) },
        ].map(m => (
          <div key={m.label} style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color: C.dim, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: m.color, fontFamily: C.mono }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Directional breakdown */}
      {state.planCount > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim }}>DIRECTIONAL BREAKDOWN</div>
            <span style={{ fontSize: 9, color: dirColor(state.directionalBias), border: `1px solid ${dirColor(state.directionalBias)}40`, borderRadius: 2, padding: '1px 5px', textTransform: 'uppercase', marginLeft: 'auto' }}>
              {state.directionalBias}
            </span>
          </div>
          <div style={{ display: 'flex', height: 10, borderRadius: 3, overflow: 'hidden', marginBottom: 8, background: '#1A3050' }}>
            {state.longRiskPct  > 0 && <div style={{ flex: state.longRiskPct,  background: C.green, transition: 'flex 0.3s' }} />}
            {state.shortRiskPct > 0 && <div style={{ flex: state.shortRiskPct, background: C.red,   transition: 'flex 0.3s' }} />}
          </div>
          <div style={{ display: 'flex', gap: 20, fontSize: 10 }}>
            <span><span style={{ color: C.green, fontWeight: 700 }}>{state.longRiskPct}%</span><span style={{ color: C.muted }}> LONG</span></span>
            <span><span style={{ color: C.red, fontWeight: 700 }}>{state.shortRiskPct}%</span><span style={{ color: C.muted }}> SHORT</span></span>
            {state.dollarRiskTotal ? <span style={{ color: C.muted }}>Est. risk: <span style={{ color: C.text, fontFamily: C.mono }}>${state.dollarRiskTotal.toFixed(0)}</span></span> : null}
            {adjDiff && <span style={{ color: C.amber, fontSize: 9 }}>Vol adjustment active</span>}
          </div>
        </div>
      )}

      {/* Portfolio Guidance — clearly separated from trace */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>PORTFOLIO GUIDANCE</div>
        {intel.guidance.map((g, i) => {
          const isWarning = g.startsWith('⊗') || g.startsWith('⚠');
          const isGood    = g.startsWith('✓');
          const gc = isWarning ? C.red : isGood ? C.green : C.text;
          return (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: gc, lineHeight: 1.7, marginBottom: 4 }}>
              <span style={{ color: isWarning ? C.red : isGood ? C.green : C.gold, flexShrink: 0 }}>›</span>
              <span>{g}</span>
            </div>
          );
        })}
      </div>

      {/* Intelligence trace — collapsed/secondary */}
      <details style={{ padding: '10px 12px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <summary style={{ fontSize: 9, letterSpacing: 2, color: C.muted, cursor: 'pointer', userSelect: 'none' }}>
          INTELLIGENCE TRACE
        </summary>
        <div style={{ marginTop: 8 }}>
          {intel.reasoning.map((r, i) => (
            <div key={i} style={{ fontSize: 9, color: C.muted, lineHeight: 1.7, fontFamily: C.mono }}>{r}</div>
          ))}
        </div>
      </details>
    </div>
  );
}

// ── Exposure tab ──────────────────────────────────────────────────────────────

function ExposureTab({ intel }: { intel: PortfolioIntelligenceOutput }) {
  const { state } = intel;
  if (state.planCount === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No active plans. Add symbols to your watchlist and run analysis.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        PLAN EXPOSURE — {state.activePlans.length} PLANS
      </div>
      {state.activePlans.map((e, i) => {
        const volAdjusted = e.adjustedRiskUnits !== e.riskUnits;
        const volFitPct   = Math.round(e.volFit * 100);
        const volFitColor = e.volFit >= 0.70 ? C.green : e.volFit >= 0.45 ? C.amber : C.red;
        return (
          <div key={i} style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
            {/* Row 1: symbol, direction, quality, weight bar, risk units */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ color: C.gold, fontWeight: 700, minWidth: 55 }}>{e.symbol}</span>
              <span style={{ color: dirColor(e.direction), fontWeight: 700, fontSize: 10 }}>{e.direction.toUpperCase()}</span>
              <span style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', border: `1px solid ${C.border}`, borderRadius: 2, padding: '1px 4px' }}>{e.qualityTier}</span>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, height: 4, background: '#1A3050', borderRadius: 2, maxWidth: 130 }}>
                  <div style={{ width: `${e.portfolioWeight * 100}%`, height: '100%', background: dirColor(e.direction), borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 10, color: C.text, fontFamily: C.mono, minWidth: 30 }}>
                  {(e.portfolioWeight * 100).toFixed(0)}%
                </span>
              </div>
              {/* Raw vs adjusted risk units */}
              <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
                <span style={{ color: C.amber, fontFamily: C.mono, fontSize: 11 }}>
                  {e.adjustedRiskUnits.toFixed(2)}R
                </span>
                {volAdjusted && (
                  <span style={{ color: C.muted, fontFamily: C.mono, fontSize: 9, textDecoration: 'line-through' }}>
                    {e.riskUnits.toFixed(2)}
                  </span>
                )}
              </div>
              {e.dollarRisk !== null && (
                <span style={{ color: C.muted, fontFamily: C.mono, fontSize: 10 }}>${e.dollarRisk.toFixed(0)}</span>
              )}
            </div>
            {/* Row 2: vol fit indicator + plan summary */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 8, color: C.muted, letterSpacing: 1 }}>VOL FIT</span>
                <div style={{ width: 40, height: 3, background: '#1A3050', borderRadius: 2 }}>
                  <div style={{ width: `${volFitPct}%`, height: '100%', background: volFitColor, borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 9, color: volFitColor, fontFamily: C.mono }}>{volFitPct}%</span>
                {volAdjusted && <span style={{ fontSize: 8, color: C.amber }}>↓adj</span>}
              </div>
              <div style={{ fontSize: 9, color: '#3A5070', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.planSummary}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Conflicts tab ─────────────────────────────────────────────────────────────

function ConflictsTab({ intel }: { intel: PortfolioIntelligenceOutput }) {
  if (intel.conflicts.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No portfolio conflicts detected. Plans are well-balanced.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        {intel.conflicts.length} CONFLICT{intel.conflicts.length > 1 ? 'S' : ''} DETECTED
      </div>
      {intel.conflicts.map((cf: ConflictSignal, i: number) => {
        const isVolInstability = cf.type === 'volatility_instability';
        const accentColor      = isVolInstability ? C.amber : sevColor(cf.severity);
        const bgOverride       = isVolInstability ? 'rgba(252,211,77,0.04)' : C.card;
        const typeIcon         = isVolInstability ? '⚡' : cf.type === 'directional' ? '↕' : cf.type === 'correlation' ? '⬡' : '⊗';
        return (
          <div key={i} style={{ padding: '12px 14px', background: bgOverride, border: `1px solid ${accentColor}40`, borderLeft: `3px solid ${accentColor}`, borderRadius: 3 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: accentColor, textTransform: 'uppercase', border: `1px solid ${accentColor}40`, borderRadius: 2, padding: '1px 5px' }}>
                {cf.severity}
              </span>
              <span style={{ fontSize: 9, color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>
                {typeIcon} {cf.type.replace(/_/g, ' ')}
              </span>
              <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
                {cf.symbols.map(s => (
                  <span key={s} style={{ fontSize: 9, color: C.gold, border: `1px solid ${C.border}`, borderRadius: 2, padding: '1px 4px' }}>{s}</span>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.text, marginBottom: 6 }}>{cf.description}</div>
            <div style={{ fontSize: 10, color: '#5A7FA8' }}>
              <span style={{ color: C.muted }}>Remedy: </span>{cf.remedy}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Priority tab ──────────────────────────────────────────────────────────────

function PriorityTab({ intel }: { intel: PortfolioIntelligenceOutput }) {
  if (intel.prioritized.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No plans to prioritize.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        PLAN PRIORITY — {intel.prioritized.length} PLANS RANKED
      </div>
      {intel.prioritized.map((p: PrioritizedPlan) => {
        const alloc = intel.allocation.find((a: AllocationGuidance) => a.symbol === p.plan.symbol);
        return (
          <div key={p.plan.symbol} style={{
            padding: '12px 14px', background: p.deprioritized ? 'rgba(30,15,15,0.4)' : C.card,
            border: `1px solid ${p.deprioritized ? '#3A1A1A' : C.border}`, borderRadius: 3,
            opacity: p.deprioritized ? 0.75 : 1,
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ color: C.muted, fontFamily: C.mono, minWidth: 20 }}>#{p.rank}</span>
              <span style={{ color: C.gold, fontWeight: 700, minWidth: 55 }}>{p.plan.symbol}</span>
              <span style={{ color: dirColor(p.plan.direction), fontSize: 10, fontWeight: 700 }}>{p.plan.direction.toUpperCase()}</span>
              <span style={{ color: p.plan.quality.tier === 'high' ? C.green : p.plan.quality.tier === 'moderate' ? C.amber : C.muted, fontSize: 9, textTransform: 'uppercase', border: `1px solid ${C.border}`, borderRadius: 2, padding: '1px 4px' }}>
                {p.plan.quality.tier}
              </span>
              {p.correlationClusterRank > 0 && (
                <span title={`${p.correlationClusterRank === 1 ? '2nd' : '3rd+'} in high-correlation cluster`} style={{ fontSize: 8, color: C.amber, border: `1px solid ${C.amber}40`, borderRadius: 2, padding: '1px 5px', letterSpacing: 0.5 }}>
                  CLUSTER {p.correlationClusterRank === 1 ? '2nd' : '3rd+'}
                </span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                {alloc && (
                  <span style={{ fontSize: 9, color: actionColor(alloc.action), fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {alloc.action.replace('_', ' ')}
                  </span>
                )}
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: C.mono }}>
                  {p.priorityScore}
                </span>
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#5A7FA8', marginBottom: alloc ? 4 : 0 }}>{p.rationale}</div>
            {alloc && (
              <div style={{ fontSize: 9, color: C.muted }}>
                Allocation: <span style={{ color: actionColor(alloc.action), fontWeight: 700 }}>{alloc.action.replace('_', ' ').toUpperCase()}</span> · <span style={{ color: C.text, fontFamily: C.mono }}>{alloc.riskUnitTarget}× risk</span> — {alloc.reason}
              </div>
            )}
            {p.deprioritized && (
              <div style={{ fontSize: 9, color: C.red, marginTop: 4 }}>⚠ {p.deprioritizeReason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main workspace ─────────────────────────────────────────────────────────────

type Tab = 'overview' | 'exposure' | 'conflicts' | 'priority';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview',  label: 'Overview'  },
  { id: 'exposure',  label: 'Exposure'  },
  { id: 'conflicts', label: 'Conflicts' },
  { id: 'priority',  label: 'Priority'  },
];

export function Portfolio() {
  const { watchlist, symbolData, settings, weights, journal } = useStore();
  const [tab, setTab] = useState<Tab>('overview');

  const riskModel: RiskModelSettings = {
    accountSize:         settings.accountSize ?? null,
    riskPerTradePct:     settings.riskPerTradePct ?? 1.0,
    maxConcurrentTrades: settings.maxConcurrentTrades ?? 5,
    maxSameThemePct:     settings.maxSameThemePct ?? 30,
    maxSameDirectionPct: settings.maxSameDirectionPct ?? 60,
  };

  // Build plans for all watchlist symbols that have data and a valid plan
  const activePlans = useMemo(() => {
    return watchlist.flatMap(w => {
      const ctx    = getSymbolContext(w.symbol, symbolData[w.symbol], settings, weights);
      const thesis = getThesis(w.symbol)?.current ?? null;
      if (!ctx) return [];
      const result = buildExecutionPlan(ctx, thesis);
      return result.primaryPlan ? [result.primaryPlan] : [];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist.length, Object.keys(symbolData).join(',')]);

  // Build execution feedbacks: match each active plan against recent journal entries
  const execFeedbacks: ExecutionFeedback[] = useMemo(() => {
    const j = journal ?? [];
    return activePlans.flatMap(plan => {
      const symEntries = j.filter(je => je.symbol === plan.symbol).slice(-3);
      return symEntries.map(je => evaluateExecution(plan, je));
    });
  }, [activePlans, journal]);

  const intel: PortfolioIntelligenceOutput = useMemo(
    () => buildPortfolioIntelligence(activePlans, execFeedbacks, riskModel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePlans.length, activePlans.map(p => p.symbol + p.direction).join(','), execFeedbacks.length],
  );

  const conflictCount = intel.conflicts.filter(c => c.severity !== 'low').length;

  return (
    <div style={{ display: 'flex', minHeight: 0, flex: '1 1 0%', flexDirection: 'column', fontFamily: C.mono }}>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 10, letterSpacing: 1.5, fontFamily: 'inherit',
            color: tab === t.id ? C.gold : C.muted,
            borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
          }}>
            {t.label.toUpperCase()}
            {t.id === 'conflicts' && conflictCount > 0 && (
              <span style={{ marginLeft: 4, fontSize: 8, color: C.red, border: `1px solid ${C.red}40`, borderRadius: 2, padding: '0 3px' }}>
                {conflictCount}
              </span>
            )}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14, gap: 8 }}>
          <span style={{ fontSize: 9, color: C.muted }}>{activePlans.length} active plan{activePlans.length !== 1 ? 's' : ''}</span>
          {intel.state.concentrationRisk !== 'safe' && (
            <span style={{ fontSize: 9, color: concColor(intel.state.concentrationRisk), border: `1px solid ${concColor(intel.state.concentrationRisk)}40`, borderRadius: 2, padding: '1px 5px' }}>
              {intel.state.concentrationRisk} concentration
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {activePlans.length === 0 ? (
          <div style={{ color: C.muted, textAlign: 'center', paddingTop: 60, fontSize: 11 }}>
            <div style={{ marginBottom: 8 }}>No active trade plans in watchlist.</div>
            <div style={{ fontSize: 10 }}>Add symbols and run analysis to populate portfolio intelligence.</div>
          </div>
        ) : (
          <>
            {tab === 'overview'  && <OverviewTab  intel={intel} />}
            {tab === 'exposure'  && <ExposureTab  intel={intel} />}
            {tab === 'conflicts' && <ConflictsTab intel={intel} />}
            {tab === 'priority'  && <PriorityTab  intel={intel} />}
          </>
        )}
      </div>
    </div>
  );
}
